import {
  Address,
  AddressModel,
  DomainModelHelper,
  EntityBase,
  RequestValidationError,
} from '@digital-platform/shared/domain';
import { DisposalSite } from '../interfaces';
import { FilterCategories } from '../interfaces/disposal-site.interface';

export class DisposalSiteModel extends EntityBase implements DisposalSite {
  constructor(config?: DisposalSite) {
    const configData = {
      ...DisposalSiteModel.DEFAULT_CONFIG,
      ...config,
    };
    super(configData);

    Object.assign(this, DisposalSiteModel.DEFAULT_CONFIG);

    if (!config) {
      return;
    }

    this.id = configData
      ? DomainModelHelper.ensureDataIsString(configData.id)
      : DisposalSiteModel.DEFAULT_CONFIG.id;
    this.name = configData
      ? DomainModelHelper.ensureDataIsString(configData.name)
      : DisposalSiteModel.DEFAULT_CONFIG.name;
    this.class = configData
      ? DomainModelHelper.ensureDataIsStringArray(configData.class)
      : DisposalSiteModel.DEFAULT_CONFIG.class;
    this.address = new AddressModel(configData.address);
    this.divisionNumber = configData
      ? DomainModelHelper.ensureDataIsString(configData.divisionNumber)
      : DisposalSiteModel.DEFAULT_CONFIG.divisionNumber;
    this.isHazardousAccepted = configData
      ? DomainModelHelper.ensureDataIsBoolean(configData.isHazardousAccepted) ||
        false
      : DisposalSiteModel.DEFAULT_CONFIG.isHazardousAccepted;
    this.isRailcarAccepted = configData
      ? DomainModelHelper.ensureDataIsBoolean(configData.isRailcarAccepted) ||
        false
      : DisposalSiteModel.DEFAULT_CONFIG.isRailcarAccepted;
    this.isVocConcentrationRequired = configData
      ? DomainModelHelper.ensureDataIsBoolean(
          configData.isVocConcentrationRequired
        ) || false
      : DisposalSiteModel.DEFAULT_CONFIG.isVocConcentrationRequired;
    this.isDryWeightRequired = configData
      ? DomainModelHelper.ensureDataIsBoolean(configData.isDryWeightRequired) ||
        false
      : DisposalSiteModel.DEFAULT_CONFIG.isDryWeightRequired;
    this.isFriableAsbestosAccepted = configData
      ? DomainModelHelper.ensureDataIsBoolean(
          configData.isFriableAsbestosAccepted
        ) || false
      : DisposalSiteModel.DEFAULT_CONFIG.isFriableAsbestosAccepted;
    this.isNonFriableAsbestosAccepted = configData
      ? DomainModelHelper.ensureDataIsBoolean(
          configData.isNonFriableAsbestosAccepted
        ) || false
      : DisposalSiteModel.DEFAULT_CONFIG.isNonFriableAsbestosAccepted;

    const type = configData
      ? DomainModelHelper.ensureDataIsString(configData.type)
      : DisposalSiteModel.DEFAULT_CONFIG.type;
    this.filterCategories = configData.filterCategories
      ? {
          'Disposal Services':
            DomainModelHelper.ensureDataIsStringArray(
              configData.filterCategories['Disposal Services']
            ) ||
            DisposalSiteModel.DEFAULT_CONFIG.filterCategories[
              'Disposal Services'
            ],
          'Waste Types':
            DomainModelHelper.ensureDataIsStringArray(
              configData.filterCategories['Waste Types']
            ) ||
            DisposalSiteModel.DEFAULT_CONFIG.filterCategories['Waste Types'],
          'Treatment Services':
            DomainModelHelper.ensureDataIsStringArray(
              configData.filterCategories['Treatment Services']
            ) ||
            DisposalSiteModel.DEFAULT_CONFIG.filterCategories[
              'Treatment Services'
            ],
        }
      : DisposalSiteModel.DEFAULT_CONFIG.filterCategories;

    this.active = configData?.active;
    switch (type) {
      case 'landfill':
      case 'deepwell':
        this.type = type;
        break;
      case null:
        this.type = null;
        break;
      default:
        throw new RequestValidationError(
          `Unknown type of disposal site! (${type})`,
          {
            context: {
              configData,
            },
          }
        );
    }
  }

  static readonly DEFAULT_CONFIG: DisposalSite = {
    id: null,
    name: '',
    type: null,
    class: [],
    active: false,
    address: AddressModel.DEFAULT_CONFIG,
    divisionNumber: '',
    isHazardousAccepted: false,
    isRailcarAccepted: false,
    isVocConcentrationRequired: false,
    isDryWeightRequired: false,
    isFriableAsbestosAccepted: false,
    isNonFriableAsbestosAccepted: false,
    filterCategories: {
      'Disposal Services': [],
      'Waste Types': [],
      'Treatment Services': [],
    },
  };

  readonly id: string;
  readonly type: 'landfill' | 'deepwell';
  readonly name: string;
  readonly active?: boolean;
  readonly class: string[];
  readonly address: Address;
  readonly divisionNumber: string;
  readonly isHazardousAccepted: boolean;
  readonly isRailcarAccepted: boolean;
  readonly isVocConcentrationRequired: boolean;
  readonly isDryWeightRequired: boolean;
  readonly isFriableAsbestosAccepted: boolean;
  readonly isNonFriableAsbestosAccepted: boolean;
  readonly filterCategories?: FilterCategories;
}
